import { useState } from "react";
import { useMutation } from "@apollo/client";
import { PackageLinkAssignmentResult } from "@models/ISchema";
import { packageLinkAssignment } from "../graphql/packageLinkAssignment.mutation";
import { DataReducerType } from "../context/types";

interface Props {
  origin: string;
  scenario: string;
}

type ReturnType = [
  { result: any; error: boolean; isLoading: boolean },
  (data: DataReducerType, dryRun?: boolean, skipValidations?: boolean) => Promise<void>,
];

const usePackageLinkAssignment = (props: Props): ReturnType => {
  const { scenario, origin } = props;
  const [packageLinkAssignmentMutation] = useMutation(packageLinkAssignment, {});
  const [result, setResult] = useState<PackageLinkAssignmentResult>({});
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * @param assignments - LinkPackageObj
   * @return LinkPackagePair
   */
  const buildAssignments = (assignments: DataReducerType["assignments"]) =>
    Object.values(assignments ?? {}).map(assignment => ({
      linkId: assignment?.linkId,
      packageId: assignment?.package?.id,
    }));

  /**
   * @param deletions - LinkPackageObj
   * @return LinkPackagePair
   */
  const buildDeletions = (deletions: DataReducerType["deletions"]) =>
    Object.values(deletions ?? {}).map(deletion => ({
      linkId: deletion?.linkId,
      packageId: deletion?.package?.id,
    }));

  const setInput = (
    { assignments, deletions }: DataReducerType,
    dryRun: boolean,
    skipValidations: boolean,
  ) => ({
    dryRun,
    skipValidations,
    changesets: {
      creates: buildAssignments(assignments),
      deletes: buildDeletions(deletions),
    },
  });

  const submitPackageLinkAssignment = async (
    data: DataReducerType,
    dryRun = true,
    skipValidations = true,
  ) => {
    try {
      setIsLoading(true);

      const variables = {
        scenarioId: scenario,
        originId: origin,
        input: setInput(data, dryRun, skipValidations),
      };
      const { data: responseData } = await packageLinkAssignmentMutation({ variables });
      setResult(responseData?.cube?.packageLinkAssignment);
    } catch (e) {
      setError(true);
      console.error("[PackageLinkAssignment]: ", e);
    } finally {
      setIsLoading(false);
    }
  };

  return [{ result, error, isLoading }, submitPackageLinkAssignment];
};

export default usePackageLinkAssignment;
